import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Stack,
  Avatar,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Launch as LaunchIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import useProjectService, { Project } from '../../../services/ProjectService';

/**
 * Active Projects Page
 * Shows client's currently active projects with quick actions
 */
const ActiveProjects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { getProjects } = useProjectService();

  useEffect(() => {
    const loadActiveProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projectsResult = await getProjects({ 
          status: 'active', 
          per_page: 20 
        });
        setProjects(projectsResult);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load projects';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadActiveProjects();
  }, [getProjects]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'on_hold': return 'warning';
      case 'cancelled': return 'error';
      case 'planning': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Loading active projects...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (projects.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Active Projects
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You don't have any active projects at the moment.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.href = '/onboarding'}
        >
          Start New Project
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        {projects.map((project) => (
          <Card key={project.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Typography variant="h6" component="h3" noWrap sx={{ flexGrow: 1, mr: 1 }}>
                  {project.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, project)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Stack>

              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 2, minHeight: 40 }}
              >
                {project.description}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip 
                  label={project.status} 
                  color={getStatusColor(project.status)}
                  size="small"
                />
                {project.priority && (
                  <Chip 
                    label={project.priority} 
                    variant="outlined"
                    size="small"
                  />
                )}
              </Stack>

              <Typography variant="caption" color="text.secondary" display="block">
                Created: {new Date(project.created_at).toLocaleDateString()}
              </Typography>
              
              {project.expected_end_date && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Expected End: {new Date(project.expected_end_date).toLocaleDateString()}
                </Typography>
              )}

              {/* Progress bar if progress data is available */}
              {project.completion_percentage !== undefined && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption">Progress</Typography>
                    <Typography variant="caption">{project.completion_percentage}%</Typography>
                  </Stack>
                  <LinearProgress 
                    variant="determinate" 
                    value={project.completion_percentage} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}
            </CardContent>

            <Box sx={{ p: 2, pt: 0 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<LaunchIcon />}
                  onClick={() => window.location.href = `/dashboard/client/projects/${project.id}`}
                >
                  View Details
                </Button>
                <Button
                  size="small"
                  startIcon={<MessageIcon />}
                  onClick={() => window.location.href = `/dashboard/client/communication/messages?project=${project.id}`}
                >
                  Message
                </Button>
              </Stack>
            </Box>
          </Card>
        ))}
      </Box>

      {/* Project Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedProject && (window.location.href = `/dashboard/client/projects/${selectedProject.id}`)}>
          <LaunchIcon sx={{ mr: 1 }} fontSize="small" />
          View Project
        </MenuItem>
        <MenuItem onClick={() => selectedProject && (window.location.href = `/dashboard/client/communication/messages?project=${selectedProject.id}`)}>
          <MessageIcon sx={{ mr: 1 }} fontSize="small" />
          Send Message
        </MenuItem>
        <MenuItem onClick={() => selectedProject && (window.location.href = `/dashboard/client/projects/timeline?project=${selectedProject.id}`)}>
          <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
          View Timeline
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ActiveProjects;